/**
 * Reservation Queue Schemas
 *
 * Defines the Zod schemas for the reservation waitlist (queue) feature.
 *
 * Schemas:
 * - QueueEntrySchema        — a single queue entry with derived position
 * - JoinQueueBodySchema     — request body to join the queue
 * - QueuePositionResponseSchema — caller's position and total queue depth
 * - QueueListResponseSchema — ordered list of queue entries
 * - QueueEntryGroupSchema   — one reservation's ordered queue, for the batched endpoint
 * - QueueListByReservationsQuerySchema / QueueListByReservationsResponseSchema —
 *   batched cross-reservation queue lookup for the global reservations list (CU-86cb7ctfw)
 *
 * Traceability: REQ-4.2, REQ-3.9 -> reservation-waitlist / Queue contracts and outbox event schema
 */
import { z } from 'zod';
import { QueueEntrySubtypeSchema } from './reservation-status.js';
import { TtlExpiresAtSchema } from './reservation-batch.js';

export const QueueEntrySchema = z.object({
  id: z.number().int(),
  mediaUnitId: z.number().int(),
  offerId: z.number().int().nullable(),
  holderUserId: z.number().int(),
  holderUserName: z.string(),
  subtype: QueueEntrySubtypeSchema,
  dateFrom: z.string(),
  dateTo: z.string(),
  position: z.number().int().positive(),
  createdAt: z.coerce.date(),
  /**
   * Resolved labels (CU-86cb7ctfw) — previously only offerId was exposed,
   * forcing callers to render a raw numeric id as a stand-in name (see the
   * closed TODO(CU-86cb3x6y6 follow-up) in
   * apps/web/src/routes/media-units/$id/index.tsx). Null when the entry has
   * no linked offer, or the offer has no client/salesperson set.
   */
  offerName: z.string().nullable(),
  clientId: z.number().int().nullable(),
  clientName: z.string().nullable(),
  /**
   * Snapshot of the client's brand at join time (CU-86cb7qgc3) — resolved
   * from the entry's own client_id/brand columns, NOT a live join through
   * offerId, so editing the linked offer's client/brand afterward never
   * changes what an existing entry reports. Shares clientName's client.view
   * field-visibility gate.
   */
  brand: z.string().nullable(),
  salespersonName: z.string().nullable(),
  /** The FCFS ordering anchor (reservation_queue.requested_at) — when the entry actually joined the line. */
  requestedAt: z.coerce.date(),
  /**
   * Set once this entry becomes the scheduled FCFS head after a release; null
   * while the window is still held or this entry isn't the head yet. Mirrors
   * reservation_queue.promotion_eligible_at — see packages/db schema comment.
   */
  promotionEligibleAt: z.coerce.date().nullable(),
});
export type QueueEntry = z.infer<typeof QueueEntrySchema>;

const JoinQueueBodyBaseSchema = z.object({
  mediaUnitId: z.number().int(),
  offerId: z.number().int().optional(),
  /** Required on the no-offer path; forbidden (must be omitted/null) alongside offerId — see the superRefine below. */
  clientId: z.number().int().optional(),
  /**
   * Accepts omission (undefined), explicit null, or a non-empty trimmed
   * string. Only validated on the no-offer path — see the "Client and brand
   * identity on queue join" requirement. An explicit `null` alongside
   * `offerId` is NOT a conflict (see the superRefine below).
   */
  brand: z.string().trim().min(1).nullable().optional(),
  subtype: QueueEntrySubtypeSchema,
  dateFrom: z.string(),
  dateTo: z.string(),
  /**
   * REQUIRED target expiry (CU-86cbc4t4q) — the same shared "do kiedy" value
   * batch-reserve and lock-reservations carry, under the same field name and
   * wire shape (see TtlExpiresAtSchema). Snapshotted onto the created queue
   * entry so a later promotion honours the caller's intent — see
   * reservation-waitlist "Queue entry carries the caller's target expiry".
   */
  ttlExpiresAt: TtlExpiresAtSchema,
});

export const JoinQueueBodySchema = JoinQueueBodyBaseSchema.superRefine((val, ctx) => {
  if (val.offerId != null && (val.clientId != null || val.brand != null)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['clientId'],
      message: 'clientId/brand must not be supplied together with offerId',
    });
  }
});
export type JoinQueueBody = z.infer<typeof JoinQueueBodySchema>;

export const QueuePositionResponseSchema = z.object({
  position: z.number().int().positive(),
  total: z.number().int().nonnegative(),
});
export type QueuePositionResponse = z.infer<typeof QueuePositionResponseSchema>;

export const QueueListResponseSchema = z.object({
  items: z.array(QueueEntrySchema),
  total: z.number().int().nonnegative(),
});
export type QueueListResponse = z.infer<typeof QueueListResponseSchema>;

// ─── Batched cross-reservation queue lookup (CU-86cb7ctfw) ────────────────────

/** One reservation's ordered FCFS waiting line, for the batched endpoint below. */
export const QueueEntryGroupSchema = z.object({
  reservationId: z.number().int().positive(),
  entries: z.array(QueueEntrySchema),
});
export type QueueEntryGroup = z.infer<typeof QueueEntryGroupSchema>;

/**
 * Query for GET /reservations/queue/by-reservations — batched queue lookup for
 * the reservations list's current page. Capped at 100 (mirrors
 * ReservationListQuerySchema.limit's max page size in reservation-list.ts):
 * this is the pagination boundary, the caller only ever asks for the ids on
 * the current page, never an unbounded set.
 */
export const QueueListByReservationsQuerySchema = z.object({
  reservationId: z
    .array(z.coerce.number().int().positive())
    .min(1)
    .max(100, 'reservationId accepts at most 100 ids per request'),
});
export type QueueListByReservationsQuery = z.infer<typeof QueueListByReservationsQuerySchema>;

export const QueueListByReservationsResponseSchema = z.object({
  groups: z.array(QueueEntryGroupSchema),
});
export type QueueListByReservationsResponse = z.infer<typeof QueueListByReservationsResponseSchema>;
